import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Lock, Check, X, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function ForcePasswordPage() {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const { api, updateUser } = useAuth();
  const navigate = useNavigate();

  const rules = [
    { label: "Minimum 8 caracteres", ok: newPw.length >= 8 },
    { label: "Au moins 1 majuscule", ok: /[A-Z]/.test(newPw) },
    { label: "Au moins 1 chiffre", ok: /[0-9]/.test(newPw) },
    { label: "Les mots de passe correspondent", ok: newPw === confirmPw && newPw.length > 0 },
  ];

  const allValid = rules.every((r) => r.ok);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allValid) {
      toast.error("Verifie les criteres du mot de passe");
      return;
    }
    setLoading(true);
    try {
      await api().post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      updateUser({ must_change_password: false });
      
      // Reset onboarding pour afficher le guide tour après la première connexion
      localStorage.removeItem("odys-onboarding-complete");
      
      toast.success("Mot de passe mis a jour !");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du changement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5] p-4" data-testid="force-password-page">
      <Card className="w-full max-w-md border-slate-200 shadow-md rounded-xl">
        <CardHeader className="text-center space-y-4 pb-2">
          <div className="mx-auto w-14 h-14 rounded-xl bg-[#16233B] flex items-center justify-center">
            <Lock className="w-7 h-7 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <CardTitle className="text-xl font-heading font-bold text-[#1F2937]">
              Change ton mot de passe
            </CardTitle>
            <CardDescription className="text-[#6B7280] mt-1">
              Pour securiser ton compte, choisis un nouveau mot de passe
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#1F2937]">Mot de passe temporaire</label>
              <Input
                data-testid="old-password-input"
                type="password"
                value={oldPw}
                onChange={(e) => setOldPw(e.target.value)}
                placeholder="Ton mot de passe actuel"
                className="h-11 rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#1F2937]">Nouveau mot de passe</label>
              <Input
                data-testid="new-password-input"
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="Ton nouveau mot de passe"
                className="h-11 rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#1F2937]">Confirmer</label>
              <Input
                data-testid="confirm-password-input"
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                placeholder="Confirme ton mot de passe"
                className="h-11 rounded-lg"
              />
            </div>

            <div className="bg-[#F5F5F5] rounded-lg p-3 space-y-2">
              {rules.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {r.ok ? (
                    <Check className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <X className="w-4 h-4 text-slate-300" />
                  )}
                  <span className={r.ok ? "text-emerald-600" : "text-[#6B7280]"}>{r.label}</span>
                </div>
              ))}
            </div>

            <Button
              type="submit"
              disabled={!allValid || loading}
              data-testid="change-password-submit-btn"
              className="w-full h-11 bg-[#16233B] hover:bg-[#162B47] text-white rounded-lg font-medium"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                "Valider mon nouveau mot de passe"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
